import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import ContactInfoFields from './components/ContactInfoFields';
import { trackShipmentEvent, trackFormSubmit, trackError } from '../../services/analyticsService';

import ReferenceDisplay from './components/ReferenceDisplay';
import FormActions from './components/FormActions';
import SuccessModal from './components/SuccessModal';
import CatalogItemsSection from './components/CatalogItemsSection';
import PaymentSection from './components/PaymentSection';
import RecipientFormSection from '../../components/RecipientFormSection';
import { shipmentItemsService } from '../../services/shipmentItemsService';
import { invoiceService } from '../../services/invoiceService';
import { supabase } from '../../lib/supabase';
import { containerService } from '../../services/containerService';
import { uploadShipmentPhoto, savePhotoMetadata } from '../../services/shipmentStorageService';


export default function CreateNewShipment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedReference, setGeneratedReference] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  
  // ✅ Prefill client state (from navigation state)
  const [prefillClientId, setPrefillClientId] = useState(null);
  const [prefillClientInfo, setPrefillClientInfo] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  // ✅ NEW: Discount state
  const [discount, setDiscount] = useState(0);
  
  // ✅ NEW: Multiple photos state
  const [shipmentPhotoFiles, setShipmentPhotoFiles] = useState([]);
  const [shipmentPhotosUrls, setShipmentPhotosUrls] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [error, setError] = useState('');

  // ✅ NEW: Payment state
  const [paymentStatus, setPaymentStatus] = useState('non_paye');
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  const [formData, setFormData] = useState({
    reference: '',
    senderName: '',
    senderPhone: '',
    senderAddress: '',
    senderCity: '',
    senderPostalCode: '',
    senderCountry: 'France',
    receiverName: '',
    receiverPhone: '',
    receiverAddress: '',
    receiverCity: 'Abidjan',
    receiverPostalCode: '',
    receiverCountry: 'Côte d\'Ivoire',
    estimatedDeparture: '',
    estimatedDelivery: '',
    receiverCommune: '',
    receiverQuartier: '',
    receiverComplement: ''
  });

  const [errors, setErrors] = useState({});

  // Add container-related state
  const [containers, setContainers] = useState([]);
  const [selectedContainerId, setSelectedContainerId] = useState('');

  useEffect(() => {
    const reference = generateReference();
    setGeneratedReference(reference);
    setFormData(prev => ({ ...prev, reference }));
  }, []);

  // ✅ Load prefill client from navigation state
  useEffect(() => {
    const stateClientId = location?.state?.clientId;
    if (stateClientId) {
      setPrefillClientId(stateClientId);
      loadPrefillClient(stateClientId);
    }
  }, [location?.state]);

  const loadPrefillClient = async (clientId) => {
    try {
      setPrefillLoading(true);
      const { data: client, error: clientError } = await supabase
        ?.from('clients')
        ?.select('id, name, phone, email, address, city, postal_code, client_status')
        ?.eq('id', clientId)
        ?.single();

      if (clientError || !client) {
        console.warn('⚠️ Could not load prefill client:', clientError?.message);
        return;
      }

      setPrefillClientInfo(client);

      // Prefill sender fields
      setFormData(prev => ({
        ...prev,
        senderName: client?.name || prev?.senderName,
        senderPhone: client?.phone || prev?.senderPhone,
        senderAddress: client?.address || prev?.senderAddress,
        senderCity: client?.city || prev?.senderCity,
        senderPostalCode: client?.postal_code || prev?.senderPostalCode,
        ...(client?.email ? { senderEmail: client?.email } : {})
      }));
    } catch (err) {
      console.warn('⚠️ Prefill client load error:', err?.message);
    } finally {
      setPrefillLoading(false);
    }
  };

  const generateReference = () => {
    const year = new Date()?.getFullYear();
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `ADT-${year}-${randomNum}`;
  };

  const handleInputChange = (field) => (e) => {
    const value = e?.target?.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDateChange = (field) => (value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // ✅ NEW: Handle multiple photos upload
  const handleMultipleShipmentPhotos = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length === 0) return;

    // Validation
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB per file
    
    const validFiles = files?.filter(file => {
      if (!validTypes?.includes(file?.type)) {
        alert(`Format non supporté pour ${file?.name}. Veuillez sélectionner des images (JPEG, PNG, WEBP)`);
        return false;
      }
      if (file?.size > maxSize) {
        alert(`${file?.name} est trop volumineux. Taille maximale : 5 MB`);
        return false;
      }
      return true;
    });

    if (validFiles?.length === 0) return;

    // Add to existing files
    setShipmentPhotoFiles(prev => [...prev, ...validFiles]);

    // Create previews for new files
    const newPreviews = await Promise.all(
      validFiles?.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader?.result);
          reader?.readAsDataURL(file);
        });
      })
    );

    setPhotoPreviews(prev => [...prev, ...newPreviews]);
  };

  // ✅ NEW: Remove specific photo
  const removeShipmentPhoto = (indexToRemove) => {
    setShipmentPhotoFiles(prev => prev?.filter((_, index) => index !== indexToRemove));
    setPhotoPreviews(prev => prev?.filter((_, index) => index !== indexToRemove));
    
    // Reset file input if all photos removed
    if (shipmentPhotoFiles?.length === 1) {
      const fileInput = document.getElementById('shipment-photos-input');
      if (fileInput) fileInput.value = '';
    }
  };

  // Add useEffect to load open containers
  useEffect(() => {
    loadOpenContainers();
  }, []);

  const loadOpenContainers = async () => {
    try {
      const data = await containerService?.getOpenContainers();
      setContainers(data);
    } catch (err) {
      console.error('Error loading containers:', err);
    }
  };

  const handleReceiverFieldChange = (field, value) => {
    const fieldMapping = {
      'name': 'receiverName',
      'phone': 'receiverPhone',
      'address': 'receiverAddress',
      'city': 'receiverCity',
      'country': 'receiverCountry',
      'commune': 'receiverCommune',
      'quartier': 'receiverQuartier',
      'neighborhood': 'receiverQuartier',
      'complement': 'receiverComplement',
      'address_complement': 'receiverComplement'
    };
    
    const mappedField = fieldMapping?.[field] || field;
    setFormData(prev => ({ ...prev, [mappedField]: value }));
    
    if (errors?.[mappedField]) {
      setErrors(prev => ({ ...prev, [mappedField]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.senderName?.trim()) newErrors.senderName = 'Le nom de l\'expéditeur est requis';
    if (!formData?.senderPhone?.trim()) newErrors.senderPhone = 'Le téléphone de l\'expéditeur est requis';
    if (!formData?.senderAddress?.trim()) newErrors.senderAddress = 'L\'adresse de l\'expéditeur est requise';
    if (!formData?.senderCity?.trim()) newErrors.senderCity = 'La ville de l\'expéditeur est requise';
    if (!formData?.senderPostalCode?.trim()) newErrors.senderPostalCode = 'Le code postal de l\'expéditeur est requis';

    if (!formData?.receiverName?.trim()) newErrors.receiverName = 'Le nom du destinataire est requis';
    if (!formData?.receiverPhone?.trim()) newErrors.receiverPhone = 'Le téléphone du destinataire est requis';
    
    // ✅ FIXED: Validate based on country (Côte d'Ivoire = Abidjan specific)
    if (formData?.receiverCountry === 'Côte d\'Ivoire') {
      // ✅ MANDATORY: Commune and Quartier REQUIRED for Côte d'Ivoire
      if (!formData?.receiverCommune?.trim()) {
        newErrors.receiverCommune = 'La commune est obligatoire pour Abidjan';
      }
      if (!formData?.receiverQuartier?.trim()) {
        newErrors.receiverQuartier = 'Le quartier est obligatoire pour Abidjan';
      }
      // City is fixed to "Abidjan", no validation needed
    } else {
      // For other countries, standard address validation
      if (!formData?.receiverAddress?.trim()) newErrors.receiverAddress = 'L\'adresse du destinataire est requise';
      if (!formData?.receiverCity?.trim()) newErrors.receiverCity = 'La ville du destinataire est requise';
    }

    if (!formData?.estimatedDeparture) newErrors.estimatedDeparture = 'La date de départ estimée est requise';
    if (!formData?.estimatedDelivery) newErrors.estimatedDelivery = 'La date de livraison estimée est requise';

    if (formData?.estimatedDeparture && formData?.estimatedDelivery) {
      const departure = new Date(formData.estimatedDeparture);
      const delivery = new Date(formData.estimatedDelivery);
      if (delivery <= departure) {
        newErrors.estimatedDelivery = 'La date de livraison doit être après la date de départ';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Track form submission start
    trackFormSubmit('create_shipment', { type: formData?.type });
    
    const isValid = validateForm();
    if (!isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // ✅ Use the already-generated reference (do NOT regenerate here)
      const reference = formData?.reference || generatedReference;

      // ✅ CLIENT RESOLUTION: CAS A — clientId fourni depuis navigation state
      let clientId = prefillClientId || null;

      if (!clientId) {
        // ✅ CAS B — création manuelle sans client présélectionné
        try {
          let existingClient = null;

          // 1. Search by normalized_phone if available
          if (formData?.senderPhone?.trim()) {
            const { data: clientByNormalizedPhone } = await supabase
              ?.from('clients')
              ?.select('id')
              ?.eq('normalized_phone', formData?.senderPhone?.trim())
              ?.maybeSingle();
            existingClient = clientByNormalizedPhone;
          }

          // 2. Fallback: search by email
          if (!existingClient && formData?.senderEmail?.trim()) {
            const { data: clientByEmail } = await supabase
              ?.from('clients')
              ?.select('id')
              ?.eq('email', formData?.senderEmail?.trim()?.toLowerCase())
              ?.maybeSingle();
            existingClient = clientByEmail;
          }

          // 3. Legacy fallback: raw phone
          if (!existingClient && formData?.senderPhone?.trim()) {
            const { data: clientByPhone } = await supabase
              ?.from('clients')
              ?.select('id')
              ?.eq('phone', formData?.senderPhone?.trim())
              ?.maybeSingle();
            existingClient = clientByPhone;
          }

          if (existingClient?.id) {
            clientId = existingClient?.id;
          } else {
            // Client does not exist — create automatically with account_status='pending'
            const newClientData = {
              name: formData?.senderName?.trim() || null,
              phone: formData?.senderPhone?.trim() || null,
              email: formData?.senderEmail?.trim()?.toLowerCase() || null,
              address: formData?.senderAddress?.trim() || null,
              city: formData?.senderCity?.trim() || null,
              account_status: 'pending',
              auth_user_id: null
            };

            const { data: createdClient, error: clientError } = await supabase
              ?.from('clients')
              ?.insert([newClientData])
              ?.select('id')
              ?.single();

            if (clientError) {
              // Handle UNIQUE constraint violation gracefully
              if (clientError?.code === '23505') {
                // Try to find the existing client again
                const { data: retryClient } = await supabase
                  ?.from('clients')
                  ?.select('id')
                  ?.eq('phone', formData?.senderPhone?.trim())
                  ?.maybeSingle();
                if (retryClient?.id) clientId = retryClient?.id;
              } else {
                console.warn('⚠️ Auto-client creation failed (non-critical):', clientError?.message);
              }
            } else {
              clientId = createdClient?.id;
            }
          }
        } catch (clientErr) {
          console.warn('⚠️ Client lookup/creation error (non-critical):', clientErr?.message);
        }
      }

      // ✅ FIXED: Build shipment data with proper Abidjan fields
      const shipmentData = {
        reference: reference,
        sender_name: formData?.senderName,
        sender_phone: formData?.senderPhone,
        sender_address: formData?.senderAddress,
        sender_city: formData?.senderCity,
        sender_country: formData?.senderCountry,
        receiver_name: formData?.receiverName,
        receiver_phone: formData?.receiverPhone,
        receiver_country: formData?.receiverCountry,
        estimated_delivery_date: formData?.estimatedDelivery,
        current_status: 'pending',
        container_id: selectedContainerId || null,
        paid_amount: paidAmount || 0,
        payment_status: paymentStatus,
        payment_method: paymentMethod || null,
        payment_notes: paymentNotes || null,
        // ✅ Link shipment to auto-created or existing client
        client_id: clientId || null,
        // ✅ FIXED: Abidjan-specific data structure
        ...(formData?.receiverCountry === 'Côte d\'Ivoire' ? {
          receiver_city: 'Abidjan', // ✅ FIXED to Abidjan
          delivery_commune: formData?.receiverCommune,
          delivery_quartier: formData?.receiverQuartier,
          delivery_complement: formData?.receiverComplement || null,
          // ✅ Build combined address for display purposes
          receiver_address: `${formData?.receiverCommune}, ${formData?.receiverQuartier}${formData?.receiverComplement ? ' - ' + formData?.receiverComplement : ''}`
        } : {
          receiver_city: formData?.receiverCity,
          receiver_address: formData?.receiverAddress
        })
      };

      const { data: newShipment, error: shipmentError } = await supabase
        ?.from('shipments')
        ?.insert([shipmentData])
        ?.select()
        ?.single();

      if (shipmentError) throw shipmentError;

      // ✅ CONVERSION PROSPECT → CLIENT (uniquement après INSERT réussi)
      if (clientId) {
        try {
          const { data: clientRecord } = await supabase
            ?.from('clients')
            ?.select('client_status')
            ?.eq('id', clientId)
            ?.single();

          const prospectStatuses = ['prospect', 'prospect_inscrit'];
          if (clientRecord && prospectStatuses?.includes(clientRecord?.client_status)) {
            const { error: updateStatusError } = await supabase
              ?.from('clients')
              ?.update({ client_status: 'client_avec_envoi' })
              ?.eq('id', clientId);

            if (updateStatusError) {
              // Log but do NOT rollback shipment — shipment is the source of truth
              console.error('⚠️ Prospect→Client conversion failed (shipment preserved):', updateStatusError?.message);
            }
          }
        } catch (conversionErr) {
          console.error('⚠️ Prospect→Client conversion error (shipment preserved):', conversionErr?.message);
        }
      }

      // Track successful shipment creation
      trackShipmentEvent('created', {
        id: newShipment?.id,
        reference: newShipment?.reference,
        status: newShipment?.status
      });

      const { data: shipmentDataFetch, error: fetchError } = await supabase?.from('shipments')?.select('id')?.eq('reference', reference)?.single();

      if (fetchError) {
        console.error('Error fetching created shipment:', fetchError);
        console.warn('⚠️ Shipment created but could not fetch ID. Showing success anyway.');
      }

      // Continue with items and invoice creation if we have shipment ID
      if (shipmentDataFetch?.id && selectedItems?.length > 0) {
        await shipmentItemsService?.create(shipmentDataFetch?.id, selectedItems);

        const pdfBlob = await invoiceService?.generateInvoicePDF({
          reference: formData?.reference,
          senderName: formData?.senderName,
          senderAddress: formData?.senderAddress,
          senderCity: formData?.senderCity,
          senderCountry: formData?.senderCountry,
          senderPhone: formData?.senderPhone,
          receiverName: formData?.receiverName,
          receiverAddress: formData?.receiverAddress,
          receiverCity: formData?.receiverCity,
          receiverCountry: formData?.receiverCountry,
          receiverPhone: formData?.receiverPhone,
          discount_amount: discount // ✅ FIX: Pass discount to PDF generation
        }, selectedItems);

        const { signedUrl, filePath } = await invoiceService?.uploadInvoice(formData?.reference, pdfBlob);

        await invoiceService?.updateShipmentInvoiceUrl(shipmentDataFetch?.id, signedUrl);

        await invoiceService?.createInvoiceFileRecord(shipmentDataFetch?.id, signedUrl, filePath);
      }

      // ✅ Upload photos and insert into shipment_files with file_type='photo'
      if (shipmentDataFetch?.id && shipmentPhotoFiles?.length > 0) {
        try {
          setIsUploadingPhotos(true);
          const uploadedPaths = [];
          for (const photoFile of shipmentPhotoFiles) {
            const filePath = await uploadShipmentPhoto(photoFile, shipmentDataFetch?.id);
            uploadedPaths?.push(filePath);
            // Insert into shipment_files with file_type='photo'
            await savePhotoMetadata(shipmentDataFetch?.id, filePath, photoFile?.name, photoFile?.size);
          }
          // Also update shipments.photos_urls for backward compatibility
          if (uploadedPaths?.length > 0) {
            await supabase?.from('shipments')?.update({ photos_urls: uploadedPaths })?.eq('id', shipmentDataFetch?.id);
          }
        } catch (photoErr) {
          console.warn('⚠️ Photos upload failed (non-critical):', photoErr);
        }
      }

      // ✅ Always show success modal if INSERT succeeded
      setShowSuccessModal(true);
    } catch (err) {
      // ✅ Only show error if INSERT actually failed
      if (err?.code === '23505') {
        alert('Erreur: Cette référence existe déjà. Veuillez réessayer.');
      } else {
        alert(`Erreur lors de la création de l'envoi: ${err?.message}`);
      }
      console.error('Shipment creation error:', err);
      trackError('shipment_creation_error', err?.message, { reference: formData?.reference });
    } finally {
      setIsSubmitting(false);
      setIsUploadingPhotos(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler ? Toutes les données saisies seront perdues.')) {
      navigate('/admin-shipments-dashboard');
    }
  };

  const handleViewShipment = () => {
    // ✅ FIX: Use the SAME route as the working "Voir" button in dashboard
    // This navigates to the tracking page, exactly like ShipmentTableRow does
    navigate(`/tracking/${generatedReference}`);
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate('/admin-shipments-dashboard');
  };

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/admin-shipments-dashboard' },
    { label: 'Nouveau envoi', path: '/create-new-shipment' }
  ];

  // ✅ NEW: Calculate total amount for payment section
  const totalAmount = selectedItems?.reduce((sum, item) => sum + (item?.priceTotal || 0), 0) - discount;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <BreadcrumbTrail customItems={breadcrumbItems} />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Créer un nouvel envoi</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Remplissez les informations de l'envoi</p>
        </div>

        {/* ✅ Prefill client banner */}
        {prefillLoading && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            Chargement des informations du client...
          </div>
        )}
        {prefillClientInfo && !prefillLoading && (
          <div className="mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-green-800">
                Client existant sélectionné : {prefillClientInfo?.name}
              </p>
              <p className="text-xs text-green-700 mt-0.5">
                {prefillClientInfo?.phone}{prefillClientInfo?.email ? ` · ${prefillClientInfo?.email}` : ''}
                {prefillClientInfo?.city ? ` · ${prefillClientInfo?.city}` : ''}
                {' · '}
                <span className="font-medium">
                  {['prospect', 'prospect_inscrit']?.includes(prefillClientInfo?.client_status)
                    ? 'Prospect — deviendra Client après création de l\'envoi' :'Client existant'}
                </span>
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-sm sm:text-base text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Reference Display */}
          <ReferenceDisplay reference={generatedReference} />

          {/* Shipment Form Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Informations de l'envoi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Date de départ estimée */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Date de départ estimée *
                </label>
                <input
                  type="date"
                  value={formData?.estimatedDeparture || ''}
                  onChange={handleInputChange('estimatedDeparture')}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                />
                {errors?.estimatedDeparture && <p className="text-xs text-red-600 mt-1">{errors?.estimatedDeparture}</p>}
              </div>

              {/* Date de livraison estimée */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Date de livraison estimée *
                </label>
                <input
                  type="date"
                  value={formData?.estimatedDelivery || ''}
                  onChange={handleInputChange('estimatedDelivery')}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                />
                {errors?.estimatedDelivery && <p className="text-xs text-red-600 mt-1">{errors?.estimatedDelivery}</p>}
              </div>

              {/* Conteneur */}
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Rattacher à un conteneur
                </label>
                <select
                  value={selectedContainerId}
                  onChange={(e) => setSelectedContainerId(e?.target?.value)}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] bg-white"
                >
                  <option value="">— Aucun conteneur (optionnel) —</option>
                  {containers?.map(container => (
                    <option key={container?.id} value={container?.id}>
                      {container?.containerCode} — {container?.departurePort} → {container?.destinationPort}
                      {container?.departureDate ? ` (départ: ${new Date(container.departureDate)?.toLocaleDateString('fr-FR')})` : ''}
                    </option>
                  ))}
                </select>
                {containers?.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">Aucun conteneur ouvert disponible pour le moment.</p>
                )}
              </div>
            </div>
          </div>

          {/* Sender Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Informations expéditeur</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <ContactInfoFields
                type="sender"
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
              />
            </div>
          </div>

          {/* Recipient Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Informations destinataire</h2>
            <RecipientFormSection
              recipientData={{
                name: formData?.receiverName,
                phone: formData?.receiverPhone,
                city: formData?.receiverCity,
                country: formData?.receiverCountry,
                commune: formData?.receiverCommune,
                neighborhood: formData?.receiverQuartier,
                address: formData?.receiverAddress,
                address_complement: formData?.receiverComplement
              }}
              onFieldChange={handleReceiverFieldChange}
              errors={{
                name: errors?.receiverName,
                phone: errors?.receiverPhone,
                city: errors?.receiverCity,
                country: errors?.receiverCountry,
                commune: errors?.receiverCommune,
                neighborhood: errors?.receiverQuartier,
                address: errors?.receiverAddress
              }}
              isEditing={true}
            />
          </div>

          {/* Catalog Items */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Articles du catalogue</h2>
            <CatalogItemsSection
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              onItemsChange={setSelectedItems}
              discount={discount}
              onDiscountChange={setDiscount}
              error=""
            />
          </div>

          {/* Payment Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Paiement</h2>
            <PaymentSection
              selectedItems={selectedItems}
              discount={discount}
              setDiscount={setDiscount}
              paymentStatus={paymentStatus}
              setPaymentStatus={setPaymentStatus}
              paidAmount={paidAmount}
              setPaidAmount={setPaidAmount}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              paymentNotes={paymentNotes}
              setPaymentNotes={setPaymentNotes}
            />
          </div>

          {/* Photos Upload */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Photos de l'envoi</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ajouter des photos
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleMultipleShipmentPhotos}
                  disabled={isUploadingPhotos}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                />
              </div>

              {photoPreviews?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                  {photoPreviews?.map((preview, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeShipmentPhoto(index)}
                        className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 active:bg-red-700 transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
                        aria-label="Supprimer la photo"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <FormActions
            isSubmitting={isSubmitting}
            onCancel={() => navigate('/admin/shipments')}
          />
        </form>
      </div>
      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          reference={generatedReference}
          onClose={() => {
            setShowSuccessModal(false);
            navigate('/admin/shipments');
          }}
          onViewShipment={handleViewShipment}
        />
      )}
    </div>
  );
}